import { useEffect, useRef } from 'react';
import { cn } from '../../lib/cn';
import type { CollaboratorSummary } from '../../hooks/queries/useMessaging';

interface MentionAutocompleteProps {
  query: string;
  collaborators: CollaboratorSummary[];
  onSelect: (collaborator: CollaboratorSummary) => void;
  onClose: () => void;
}

export function MentionAutocomplete({ query, collaborators, onSelect, onClose }: MentionAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = collaborators.filter((c) =>
    c.full_name.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!filtered.length) return null;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full mb-1 left-0 w-64 bg-cime-surface border border-cime-border rounded-lg shadow-lg overflow-hidden z-50"
    >
      {filtered.slice(0, 8).map((collab) => (
        <button
          key={collab.id}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(collab);
          }}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
            'hover:bg-cime-sidebar-active-bg transition-colors',
          )}
        >
          {collab.avatar_url ? (
            <img src={collab.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <span className="w-6 h-6 rounded-full bg-cime-primary text-white flex items-center justify-center text-xs flex-shrink-0">
              {collab.full_name.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="text-cime-text-primary truncate">{collab.full_name}</span>
        </button>
      ))}
    </div>
  );
}
